import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Lifebuoy, DesktopTower, CaretLeft, MagnifyingGlass, Export, PaperPlaneRight, NotePencil, Wrench } from "@phosphor-icons/react";
import { Switch } from "@/components/ui/switch";
import { useApp } from "@/context/AppContext";
import { useIsDesktop } from "@/hooks/useIsDesktop";
import api from "@/lib/api";

const CAT_LABELS = { nfc_tag: "NFC-Tag", buddy: "SafeBuddy", qr: "QR-Code", login: "Login", group: "Gruppe", activation: "Aktivierung", shop: "Shop", premium: "Premium", other: "Sonstiges" };
const STATUS_LABELS = { OPEN: "Offen", IN_PROGRESS: "In Bearbeitung", WAITING_FOR_USER: "Wartet auf Nutzer", RESOLVED: "Gelöst", CLOSED: "Geschlossen" };
const STATUS_COLORS = { OPEN: "#EF4444", IN_PROGRESS: "#F59E0B", WAITING_FOR_USER: "#0EA5E9", RESOLVED: "#22C55E", CLOSED: "#6B7280" };
const PRIO_LABELS = { LOW: "Niedrig", NORMAL: "Normal", HIGH: "Hoch", URGENT: "Dringend" };
const CANNED_LABELS = { received: "Eingang bestätigen", need_info: "Mehr Infos anfragen", fixed: "Als behoben melden" };

function fmt(iso) {
  try { return new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso || ""; }
}

function TicketDetail({ id, onBack, onChanged }) {
  const [data, setData] = useState(null);
  const [note, setNote] = useState("");
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState("");

  const load = useCallback(async () => {
    try { const r = await api.get(`/admin/support/tickets/${id}`); setData(r.data); }
    catch { toast.error("Ticket konnte nicht geladen werden"); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <p className="font-body text-sm text-gray-500">…</p>;
  const t = data.ticket;

  const update = async (body) => {
    setBusy(true);
    try { await api.put(`/admin/support/tickets/${id}`, body); await load(); onChanged && onChanged(); toast.success("Aktualisiert"); }
    catch { toast.error("Fehler"); }
    finally { setBusy(false); }
  };
  const runAction = async (body) => {
    setBusy(true);
    try { const r = await api.post(`/admin/support/tickets/${id}/action`, body); toast.success(r.data.message || "Erledigt"); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="admin-ticket-detail">
      <button data-testid="ticket-back" onClick={onBack} className="font-body text-sm text-gray-500 flex items-center gap-1"><CaretLeft size={14} />Alle Tickets</button>

      <div className="border-2 border-gray-800 rounded-2xl p-4 bg-white">
        <div className="flex items-center justify-between">
          <span className="font-heading font-bold">{t.ticket_no}</span>
          <span className="text-[10px] uppercase px-2 py-0.5 rounded-full text-white" style={{ background: STATUS_COLORS[t.status] }}>{STATUS_LABELS[t.status]}</span>
        </div>
        <p className="font-body text-xs text-gray-500 mt-1">{t.user_email} · {CAT_LABELS[t.category]} · {fmt(t.created_at)}</p>
        <p className="font-body text-sm text-gray-900 mt-2 whitespace-pre-wrap">{t.description || "—"}</p>
        {t.tech?.error_id && <p className="font-body text-[11px] text-gray-400 mt-2">Fehler-ID: {t.tech.error_id}</p>}
      </div>

      {t.screenshot_path && (
        <a href={t.screenshot_path} target="_blank" rel="noreferrer" data-testid="ticket-screenshot">
          <img src={t.screenshot_path} alt="Screenshot" className="rounded-xl border-2 border-gray-800 max-h-48 object-contain" />
        </a>
      )}

      {t.tech && Object.keys(t.tech).length > 0 && (
        <details className="border-2 border-gray-800 rounded-2xl p-3 bg-white" data-testid="ticket-tech">
          <summary className="cursor-pointer font-heading text-sm">Technischer Kontext</summary>
          <div className="mt-2 space-y-0.5 font-body text-[11px] text-gray-600 break-words">
            {Object.entries(t.tech).map(([k, v]) => <p key={k}><strong>{k}:</strong> {String(v)}</p>)}
          </div>
        </details>
      )}

      {data.diagnosis && (
        <div className="border-2 border-sky-300 bg-sky-50 rounded-2xl p-3" data-testid="ticket-diagnosis">
          <p className="font-heading text-sm font-semibold flex items-center gap-1"><Wrench size={16} />Tag-Diagnose</p>
          {data.diagnosis.exists ? (
            <div className="font-body text-xs text-gray-700 mt-1 space-y-0.5">
              <p>Status: <strong>{data.diagnosis.status}</strong>{data.diagnosis.blocked ? " (gesperrt)" : ""}</p>
              <p>Zugeordnet: {data.diagnosis.assigned ? "ja" : "nein"} · Scans: {data.diagnosis.scans}</p>
              {data.diagnosis.code && <p>Code: {data.diagnosis.code}</p>}
            </div>
          ) : <p className="font-body text-xs text-gray-700 mt-1">Kein zugehöriger Tag gefunden.</p>}
        </div>
      )}

      {data.suggestions?.length > 0 && (
        <div className="border-2 border-gray-800 rounded-2xl p-3 bg-white">
          <p className="font-heading text-sm font-semibold mb-1">Lösungsvorschläge</p>
          <ul className="list-disc pl-5 font-body text-xs text-gray-600 space-y-0.5">{data.suggestions.map((s, i) => <li key={i}>{s}</li>)}</ul>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <label className="font-body text-xs text-gray-600">Status
          <select data-testid="ticket-status-select" disabled={busy} value={t.status} onChange={(e) => update({ status: e.target.value })} className="sb-input mt-1">
            {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <label className="font-body text-xs text-gray-600">Priorität
          <select data-testid="ticket-priority-select" disabled={busy} value={t.priority} onChange={(e) => update({ priority: e.target.value })} className="sb-input mt-1">
            {Object.entries(PRIO_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
      </div>

      <div className="border-2 border-gray-800 rounded-2xl p-3 bg-white space-y-2">
        <p className="font-heading text-sm font-semibold flex items-center gap-1"><PaperPlaneRight size={16} />Antwort an Nutzer</p>
        <div className="flex flex-wrap gap-1.5">
          {Object.entries(CANNED_LABELS).map(([k, l]) => (
            <button key={k} data-testid={`canned-${k}`} disabled={busy} onClick={() => update({ reply: k })} className="sb-btn px-2 py-1 bg-white text-xs">{l}</button>
          ))}
        </div>
        <textarea data-testid="ticket-reply" className="sb-input" rows={2} placeholder="Eigene Antwort (per E-Mail)" value={reply} onChange={(e) => setReply(e.target.value)} />
        <button data-testid="ticket-reply-send" disabled={busy || !reply.trim()} onClick={() => { update({ reply }); setReply(""); }} className="sb-btn w-full py-2 text-white text-sm font-medium disabled:opacity-50" style={{ background: "var(--app-primary)" }}>Antwort senden</button>
        {t.replies?.length > 0 && <div className="space-y-1 pt-1">{t.replies.map((r, i) => <p key={i} className="font-body text-[11px] text-gray-500 border-l-2 border-gray-300 pl-2">{r.text} <span className="text-gray-400">— {fmt(r.at)}</span></p>)}</div>}
      </div>

      <div className="border-2 border-gray-800 rounded-2xl p-3 bg-white space-y-2">
        <p className="font-heading text-sm font-semibold flex items-center gap-1"><NotePencil size={16} />Interne Notiz</p>
        <textarea data-testid="ticket-note" className="sb-input" rows={2} placeholder="Nur für Admins sichtbar" value={note} onChange={(e) => setNote(e.target.value)} />
        <button data-testid="ticket-note-add" disabled={busy || !note.trim()} onClick={() => { update({ note }); setNote(""); }} className="sb-btn w-full py-2 bg-white text-sm font-medium disabled:opacity-50">Notiz hinzufügen</button>
        {t.admin_notes?.length > 0 && <div className="space-y-1 pt-1">{t.admin_notes.map((n, i) => <p key={i} className="font-body text-[11px] text-gray-600 bg-amber-50 rounded-lg px-2 py-1">{n.text} <span className="text-gray-400">— {n.by}, {fmt(n.at)}</span></p>)}</div>}
      </div>

      <details className="border-2 border-gray-800 rounded-2xl p-3 bg-white" data-testid="ticket-actions">
        <summary className="cursor-pointer font-heading text-sm font-semibold flex items-center gap-1"><Wrench size={16} />Schnellaktionen</summary>
        <div className="mt-2 space-y-2">
          <button data-testid="action-send-reset" disabled={busy} onClick={() => runAction({ action: "send_reset" })} className="sb-btn w-full py-2 bg-white text-sm">Passwort-Reset-Link senden</button>
          <button data-testid="action-extend-guest" disabled={busy} onClick={() => runAction({ action: "extend_guest", days: 3 })} className="sb-btn w-full py-2 bg-white text-sm">Gastzugang +3 Tage</button>
          <div className="flex gap-2">
            <input data-testid="action-code-input" className="sb-input" placeholder="Aktivierungscode" value={code} onChange={(e) => setCode(e.target.value)} />
            <button data-testid="action-deactivate-code" disabled={busy || !code.trim()} onClick={() => runAction({ action: "deactivate_code", code })} className="sb-btn px-3 bg-white text-sm shrink-0 disabled:opacity-50">Deaktivieren</button>
          </div>
        </div>
      </details>
    </div>
  );
}

export function AdminSupport() {
  const { setSupportEnabled } = useApp();
  const isDesktop = useIsDesktop();
  const [enabled, setEnabled] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [data, setData] = useState({ total: 0, counts: {}, tickets: [] });
  const [statusFilter, setStatusFilter] = useState("");
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState(null);

  const loadSettings = useCallback(() => {
    api.get("/admin/support/settings").then((r) => setEnabled(!!r.data.enabled)).catch(() => {}).finally(() => setLoaded(true));
  }, []);
  const loadTickets = useCallback(() => {
    api.get("/admin/support/tickets").then((r) => setData(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadSettings(); loadTickets(); }, [loadSettings, loadTickets]);

  const toggle = async (v) => {
    setEnabled(v);
    try { const r = await api.put("/admin/support/settings", { enabled: v }); setEnabled(!!r.data.enabled); setSupportEnabled && setSupportEnabled(!!r.data.enabled); toast.success(v ? "Support aktiviert" : "Support deaktiviert"); }
    catch { toast.error("Fehler"); setEnabled(!v); }
  };

  const exportCsv = () => {
    const rows = [["Ticket", "E-Mail", "Kategorie", "Status", "Priorität", "Erstellt", "Beschreibung"]];
    data.tickets.forEach((t) => rows.push([t.ticket_no, t.user_email, CAT_LABELS[t.category] || t.category, STATUS_LABELS[t.status] || t.status, PRIO_LABELS[t.priority] || t.priority, fmt(t.created_at), (t.description || "").replace(/[\n;]/g, " ")]));
    const csv = "\uFEFF" + rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(";")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "support-tickets.csv"; a.click(); URL.revokeObjectURL(url);
  };

  const filtered = data.tickets.filter((t) => {
    if (statusFilter && t.status !== statusFilter) return false;
    const s = q.trim().toLowerCase();
    if (s && !(t.ticket_no.toLowerCase().includes(s) || (t.user_email || "").toLowerCase().includes(s) || (t.description || "").toLowerCase().includes(s))) return false;
    return true;
  });

  return (
    <div className="sb-card p-5" data-testid="admin-support">
      <h2 className="text-lg font-heading font-semibold mb-1 flex items-center gap-2"><Lifebuoy size={22} weight="duotone" /> Support-Tickets</h2>
      <p className="font-body text-xs text-gray-500 mb-4">Nutzeranfragen bearbeiten, diagnostizieren und beantworten.</p>

      <div className="flex items-center justify-between border-2 border-gray-800 rounded-2xl px-4 py-3 bg-white mb-4">
        <div>
          <p className="font-body font-medium">Support-Bereich für Nutzer</p>
          <p className="font-body text-xs text-gray-500 mt-0.5">Zeigt FAQ &amp; Ticket-Erstellung in der App</p>
        </div>
        <Switch data-testid="flag-support-toggle" checked={enabled} disabled={!loaded} onCheckedChange={toggle} />
      </div>

      {!isDesktop ? (
        <div className="text-center py-6" data-testid="support-desktop-only">
          <DesktopTower size={40} weight="duotone" className="mx-auto text-gray-400" />
          <p className="mt-2 font-heading text-sm text-gray-500">Die Ticket-Verwaltung ist nur am Desktop verfügbar.<br />Bitte öffne diesen Bereich an einem größeren Bildschirm.</p>
        </div>
      ) : openId ? (
        <TicketDetail id={openId} onBack={() => { setOpenId(null); loadTickets(); }} onChanged={loadTickets} />
      ) : (
        <>
          <div className="flex flex-wrap gap-2 mb-3" data-testid="ticket-counts">
            {["OPEN", "IN_PROGRESS", "WAITING_FOR_USER", "RESOLVED", "CLOSED"].map((s) => (
              <button key={s} data-testid={`filter-${s}`} onClick={() => setStatusFilter(statusFilter === s ? "" : s)}
                className={`px-2.5 py-1 rounded-full text-xs font-heading border-2 ${statusFilter === s ? "text-white border-gray-800" : "bg-white text-gray-600 border-gray-300"}`}
                style={statusFilter === s ? { background: STATUS_COLORS[s] } : {}}>
                {STATUS_LABELS[s]} ({data.counts[s] || 0})
              </button>
            ))}
          </div>
          <div className="flex gap-2 mb-3">
            <div className="relative flex-1">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input data-testid="ticket-search" className="sb-input pl-9" placeholder="Suche (Nr., E-Mail, Text)" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
            <button data-testid="ticket-export" onClick={exportCsv} className="sb-btn px-3 bg-white text-sm shrink-0 flex items-center gap-1"><Export size={16} />CSV</button>
          </div>

          {filtered.length === 0 && <p className="font-body text-sm text-gray-500">Keine Tickets.</p>}
          <div className="space-y-2" data-testid="ticket-list">
            {filtered.map((t) => (
              <button key={t.id} data-testid={`ticket-row-${t.ticket_no}`} onClick={() => setOpenId(t.id)} className="w-full text-left border-2 border-gray-800 rounded-2xl px-3 py-2 bg-white active:scale-[0.99] transition-transform">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-heading text-sm font-bold">{t.ticket_no}</span>
                  <span className="text-[10px] uppercase px-2 py-0.5 rounded-full text-white shrink-0" style={{ background: STATUS_COLORS[t.status] }}>{STATUS_LABELS[t.status]}</span>
                </div>
                <p className="font-body text-xs text-gray-500 truncate">{t.user_email} · {CAT_LABELS[t.category]} · {PRIO_LABELS[t.priority]}</p>
                <p className="font-body text-xs text-gray-700 truncate mt-0.5">{t.description || "—"}</p>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
